import logger from '@/modules/Logger';
import store from '@/store/index';

const REFRESH_RETRY_DELAYS = [2000, 4000, 6000, 8000];

export default {
  /**
   * After auto-reconnection, the remote MediaStreamTrack may have ended
   * even though Agora SDK still holds a valid subscription. This method
   * re-subscribes to the remote user's tracks and rebuilds the stream.
   */
  async refreshRemoteStream() {
    if (!this.remoteUserRtc) {
      return false;
    }

    const uid = this.remoteUserRtc.uid;

    // Check if the existing remote video track is stale (ended) or missing
    const existingEntry = this.remoteUsers.find(u => u.peerId === uid);
    if (!existingEntry || !existingEntry.stream) {
      // No existing entry — will try to subscribe and create one
    } else {
      const videoTracks = existingEntry.stream.getVideoTracks();
      const allAlive = videoTracks.length > 0 && videoTracks.every(t => t.readyState === 'live');
      if (allAlive) {
        // Tracks are still alive — no refresh needed
        return true;
      }
    }

    logger.info('[Agora] Stale remote track detected after reconnection, re-subscribing', {
      operation: 'webrtc:refresh-remote-stream',
      lesson_id: store.state.lessonId
    });

    try {
      // Unsubscribe first to force Agora to release the stale track,
      // then re-subscribe to get a fresh one from the server
      if (this.remoteUserRtc.hasVideo) {
        try {
          await this.rtc.unsubscribe(this.remoteUserRtc, 'video');
        } catch (e) {
          // Ignore unsubscribe errors — track may already be gone
        }
        await this.rtc.subscribe(this.remoteUserRtc, 'video');
      }
      if (this.remoteUserRtc.hasAudio) {
        try {
          await this.rtc.unsubscribe(this.remoteUserRtc, 'audio');
        } catch (e) {
          // Ignore unsubscribe errors
        }
        await this.rtc.subscribe(this.remoteUserRtc, 'audio');
      }

      // Rebuild remote tracks and stream from the refreshed subscription
      this.remoteUserTrack = [];
      const remoteStream = new MediaStream();

      if (this.remoteUserRtc.hasVideo && this.remoteUserRtc.videoTrack) {
        this.remoteUserTrack.push(this.remoteUserRtc.videoTrack);
        const mediaTrack = this.remoteUserRtc.videoTrack.getMediaStreamTrack();
        if (mediaTrack && mediaTrack.readyState === 'live') {
          remoteStream.addTrack(mediaTrack);
        }
      }

      if (this.remoteUserRtc.hasAudio && this.remoteUserRtc.audioTrack) {
        this.remoteUserTrack.push(this.remoteUserRtc.audioTrack);
        const mediaTrack = this.remoteUserRtc.audioTrack.getMediaStreamTrack();
        if (mediaTrack && mediaTrack.readyState === 'live') {
          remoteStream.addTrack(mediaTrack);
        }
      }

      if (remoteStream.getTracks().length === 0) {
        logger.info('[Agora] Re-subscribe completed but no live tracks yet', {
          operation: 'webrtc:refresh-remote-stream',
          lesson_id: store.state.lessonId
        });
        return false;
      }

      // Replace the old entry (if exists) with the fresh stream
      this.remoteUserExists(uid);
      this.remoteUsers.push({ peerId: uid, stream: remoteStream });

      // Play audio track if available
      if (this.remoteUserRtc.hasAudio && this.remoteUserRtc.audioTrack) {
        this.playRemoteAudioTrack();
      }

      logger.info('[Agora] Remote stream refreshed successfully via re-subscribe', {
        operation: 'webrtc:refresh-remote-stream',
        trackCount: remoteStream.getTracks().length,
        lesson_id: store.state.lessonId
      });
      return true;
    } catch (err) {
      logger.error('[Agora] Failed to re-subscribe remote tracks after reconnection', {
        operation: 'webrtc:refresh-remote-stream',
        err: err.message,
        lesson_id: store.state.lessonId
      });
      return false;
    }
  },

  /**
   * Retry refreshRemoteStream with increasing delays.
   * Agora SDK may need a few seconds to deliver fresh tracks after reconnection.
   */
  refreshRemoteStreamWithRetry(attempt) {
    if (attempt >= REFRESH_RETRY_DELAYS.length) {
      return;
    }
    setTimeout(async () => {
      const success = await this.refreshRemoteStream();
      if (!success) {
        this.refreshRemoteStreamWithRetry(attempt + 1);
      }
    }, REFRESH_RETRY_DELAYS[attempt]);
  }
};
